/**
 * Minimal hidden grid component for Excel export
 *
 * This component creates an AG Grid instance off-screen for the purpose of
 * exporting data. It uses the same column/row building utilities as PlanEditor
 * but without the UI complexity (validation, dialogs, context providers, etc.)
 */

import { logger } from "@platform/browser-logger";
import {
  ColDef,
  ColGroupDef,
  GridApi,
  GridReadyEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useEffect, useRef, useState } from "react";
import {
  BenefitDefinition,
  BenefitPlanGroup,
  DataRow,
  LocCoverageType,
  RfpCoverage,
  buildDataRows,
  getBenefitDefinitions,
  getPlanEditorTheme,
} from "@platform/plan-grid-core";
import { buildColumns, EditColumnStrategy } from "@platform/plan-grid-core";

import { hideProposalOnlyRows } from "../util/hideProposalOnlyRows";

interface HiddenPlanGridProps {
  /** Coverage data for this LOC */
  coverage: RfpCoverage;
  /** Benefit plan groups for this LOC */
  benefitPlanGroups: BenefitPlanGroup[];
  /** Callback when grid is ready */
  onGridReady: (gridApi: GridApi) => void;
  /** Hide the per-plan Current Carrier row (BTX_CURRENT_CARRIER_POLICY_DROPDOWN). */
  hideCurrentCarrierRow?: boolean;
  /** Resolved "Carrier - PolicyNumber" label keyed by plan id, for the In-Force Policy Name row. */
  resolvedPolicyLabelByPlanId?: Record<string, string>;
  /**
   * Show Voluntary Life's flexible childBenefitAmountsByAge row instead of the
   * three fixed child benefit-description rows (btx-ch-benefits-field, CD-1976
   * parity), so the export matches what the analyst sees on screen.
   */
  childAgeBandsFieldEnabled?: boolean;
}

// Plan columns are keyed by plan id (see buildPlanGroupColumns `field: plan.id`).
const buildInForcePolicyNameRow = (
  benefitPlanGroups: BenefitPlanGroup[],
  resolvedPolicyLabelByPlanId: Record<string, string>,
): DataRow => {
  const row: DataRow = {
    benefit: { label: "Carrier Name" } as BenefitDefinition,
    benefitKey: "inForcePolicyName",
  };
  benefitPlanGroups.forEach((group) => {
    group.benefitPlans.forEach((plan) => {
      const label = resolvedPolicyLabelByPlanId[plan.id];
      if (label) {
        row[plan.id] = label;
      }
    });
  });
  return row;
};

/**
 * Hidden grid component for exporting benefit plans
 *
 * Renders an AG Grid instance off-screen with the same structure as PlanEditor.
 */
export const HiddenPlanGrid: React.FC<HiddenPlanGridProps> = ({
  coverage,
  benefitPlanGroups,
  onGridReady,
  hideCurrentCarrierRow,
  resolvedPolicyLabelByPlanId,
  childAgeBandsFieldEnabled,
}) => {
  const gridRef = useRef<AgGridReact>(null);
  const [columnDefs, setColumnDefs] = useState<(ColDef | ColGroupDef)[]>([]);
  const [rowData, setRowData] = useState<DataRow[]>([]);
  const [isReady, setIsReady] = useState(false);

  // Build columns and rows on mount
  useEffect(() => {
    const buildGridData = async (): Promise<void> => {
      try {
        logger.info("[btx export] Building grid data for coverage:", {
          coverageType: coverage.type,
        });

        const benefitDefinitions = await getBenefitDefinitions(
          coverage.type as LocCoverageType,
        );

        // Build columns
        const cols = buildColumns(
          benefitPlanGroups,
          coverage,
          false,
          new EditColumnStrategy(),
        );
        setColumnDefs(cols);

        // Build rows
        const rows = hideProposalOnlyRows(
          buildDataRows(
            coverage,
            benefitDefinitions,
            cols,
            hideCurrentCarrierRow,
          ),
          { childAgeBandsFieldEnabled },
        );

        // Prepend the In-Force Policy Name row only when it carries values, so a
        // coverage with no in-force policy (e.g. Dental HMO, no current plans)
        // matches the benefits table — which drops it via buildDataRows'
        // hasCurrentPlans gate.
        const inForcePolicyNameRow =
          hideCurrentCarrierRow && resolvedPolicyLabelByPlanId
            ? buildInForcePolicyNameRow(
                benefitPlanGroups,
                resolvedPolicyLabelByPlanId,
              )
            : null;
        const inForcePolicyNameRowHasValues =
          inForcePolicyNameRow !== null &&
          Object.keys(inForcePolicyNameRow).some(
            (key) => key !== "benefit" && key !== "benefitKey",
          );
        setRowData(
          inForcePolicyNameRowHasValues
            ? [inForcePolicyNameRow, ...rows]
            : rows,
        );

        logger.info("[btx export] Grid data built:", {
          coverageType: coverage.type,
          columnCount: cols.length,
          rowCount: inForcePolicyNameRowHasValues
            ? rows.length + 1
            : rows.length,
        });

        setIsReady(true);
      } catch (error) {
        logger.error("[btx export] Error building grid data:", error as Error);
      }
    };

    buildGridData();
  }, [
    coverage,
    benefitPlanGroups,
    hideCurrentCarrierRow,
    resolvedPolicyLabelByPlanId,
    childAgeBandsFieldEnabled,
  ]);

  const handleGridReady = (event: GridReadyEvent): void => {
    logger.info(
      "[btx export] Grid ready event received, waiting for columns to initialize",
    );
    // Use a small delay to ensure column definitions are fully applied.
    // AG Grid's onGridReady fires before internal column state is complete.
    // This matches the pattern used in useAgGridState.ts.
    setTimeout(() => {
      logger.info("[btx export] Grid ready - columns initialized");
      onGridReady(event.api);
    }, 100);
  };

  if (!isReady) {
    return null;
  }

  return (
    <div
      style={{
        position: "absolute",
        left: "-9999px",
        top: "-9999px",
        width: "1px",
        height: "1px",
        overflow: "hidden",
      }}
    >
      <AgGridReact
        ref={gridRef}
        theme={getPlanEditorTheme()}
        columnDefs={columnDefs}
        rowData={rowData}
        onGridReady={handleGridReady}
        excelStyles={[
          {
            id: "excel-center-align",
            alignment: {
              horizontal: "Center",
            },
          },
          {
            id: "excel-parent-row",
            font: {
              bold: true,
            },
            interior: {
              color: "#D3D3D3",
              pattern: "Solid",
            },
          },
        ]}
      />
    </div>
  );
};
